import * as constant from '../constant.js';
import * as canvasUtil from '../canvasUtil.js';
import * as mwx from '../common.js';
import {
  Image as FabricImage,
  loadSVGFromURL,
  StaticCanvas,
  util
} from 'fabric';
import {
  hasLayerImageSource,
  resolveLayerImageUrl
} from './resolveImageUrlFromImageData.js';

const noWhiteLayerType = [
  constant.layer_background,
  constant.layer_transparentGrid,
  constant.layer_stampingTemplate,
  constant.layer_stampingMaterial
];
const validWhiteModes = [
  constant.whiteModeMap.NO_BUTTON,
  constant.whiteModeMap.DEFAULT,
  constant.whiteModeMap.EMPTY
];

/**
 * 获取当前导出口径下不参与白墨的图层类型。
 * @param {Object} options - 参数对象。
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许烫色专用图层参与绘制。
 * @returns {Array<string>} 不参与白墨处理的图层类型。
 */
function getNoWhiteLayerType(options) {
  const { includeStampingLayers = false } = options || {};
  // 预览烫色效果时，烫色模板和烫色素材需要交给白墨合成链路处理。
  if (includeStampingLayers) {
    return [constant.layer_background, constant.layer_transparentGrid];
  }
  return noWhiteLayerType;
}

/**
 * 判断图片地址是否为 SVG。
 * @param {string} imageUrl - 图片地址。
 * @returns {boolean} 是否为 SVG 地址。
 */
function isSvgImageUrl(imageUrl) {
  const url = String(imageUrl || '')
    .trim()
    .toLowerCase();
  if (!url) {
    return false;
  }
  if (url.indexOf('data:image/svg+xml') === 0) {
    return true;
  }
  const cleanUrl = url.split('?')[0].split('#')[0];
  return cleanUrl.endsWith('.svg');
}

/**
 * 使用 Fabric SVG 解析能力加载 SVG 素材。
 * @param {string} imageUrl - SVG 图片地址。
 * @returns {Promise<fabric.Object>} SVG 对应的 Fabric 对象。
 */
async function loadSvgFabricObjectFromUrl(imageUrl) {
  const svgResult = await loadSVGFromURL(imageUrl, undefined, {
    crossOrigin: 'anonymous'
  });
  const objects = (svgResult && svgResult.objects) || [];
  if (!objects.length) {
    throw new Error('loadSvgFabricObjectFromUrl: SVG objects 为空');
  }
  const svgObject = util.groupSVGElements(objects, svgResult.options || {});
  svgObject.set('_fitLayerInfoSize', true);
  return svgObject;
}

/**
 * 根据图片地址类型加载 Fabric 对象。
 * @param {string} imageUrl - 图片地址。
 * @returns {Promise<fabric.Object>} Fabric 对象。
 */
async function loadFabricObjectFromImageUrl(imageUrl) {
  // SVG 地址使用 Fabric 的 SVG 解析流程，保留矢量绘制行为。
  if (isSvgImageUrl(imageUrl)) {
    try {
      return await loadSvgFabricObjectFromUrl(imageUrl);
    } catch (error) {
      console.error('loadFabricObjectFromImageUrl: SVG 加载失败', error);
      if (mwx.isDev) {
        mwx.Toast('SVG素材加载失败');
        throw error;
      }
    }
  }
  return await FabricImage.fromURL(imageUrl, {
    crossOrigin: 'anonymous'
  });
}

/**
 * 创建离屏 StaticCanvas。
 * @param {Object} options - 参数对象。
 * @param {number} options.width - 画布宽度。
 * @param {number} options.height - 画布高度。
 * @returns {fabric.StaticCanvas} 离屏 Fabric 画布。
 */
function createOffscreenStaticCanvas(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('createOffscreenStaticCanvas: width/height 无效');
  }

  if (typeof document === 'undefined') {
    throw new Error('createOffscreenStaticCanvas: document 不存在');
  }

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width;
  canvasEl.height = height;

  const offscreenCanvas = new StaticCanvas(canvasEl, {
    width: width,
    height: height
  });
  offscreenCanvas.renderOnAddRemove = false;
  return offscreenCanvas;
}

/**
 * 按 zIndex 从小到大排序图层。
 * @param {Array<Object>} layers - 图层数组。
 * @returns {Array<Object>} 排序后的新数组。
 */
function sortLayersByZIndex(layers) {
  return [...(layers || [])].sort((a, b) => {
    const zIndexA = typeof a.zIndex === 'number' ? a.zIndex : 0;
    const zIndexB = typeof b.zIndex === 'number' ? b.zIndex : 0;
    return zIndexA - zIndexB;
  });
}

/**
 * 判断是否为默认白墨状态。
 * @param {Object} layer - 图层对象。
 * @returns {boolean} 是否为默认白墨。
 */
function isDefaultWhiteInk(layer) {
  const whiteInk = layer && layer.whiteInk;
  return constant.isDefaultWhiteInkValue(whiteInk);
}

/**
 * 判断图层是否可参与白墨逐层合成。
 * @param {Object} layer - 图层对象。
 * @param {Object} [options] - 参数对象。
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许烫色专用图层参与绘制。
 * @returns {boolean} 是否可参与逐层合成。
 */
function shouldProcessWhiteInkCompositeLayer(layer, options) {
  const currentNoWhiteLayerType = getNoWhiteLayerType(options);
  return !!(
    layer &&
    !currentNoWhiteLayerType.includes(layer.type) &&
    hasLayerImageSource(layer)
  );
}

/**
 * 获取图层白墨图片地址。
 * @param {Object} layer - 图层对象。
 * @returns {string} 白墨图片地址；没有白墨时返回空字符串。
 */
function getLayerWhiteInkSource(layer) {
  const whiteInk = layer && layer.whiteInk;
  if (
    whiteInk &&
    typeof whiteInk === 'string' &&
    !constant.isClearWhiteInkValue(whiteInk) &&
    !constant.isDefaultWhiteInkValue(whiteInk)
  ) {
    return whiteInk;
  }
  return '';
}

/**
 * 获取图层白墨合成状态。
 * @param {Object} layer - 图层对象。
 * @returns {'custom'|'clear'|'default'} 白墨合成状态。
 */
function getLayerWhiteInkCompositeState(layer) {
  const whiteInk = layer && layer.whiteInk;
  // 清空白墨图层只清空累计白墨，不叠加当前白墨。
  if (constant.isClearWhiteInkValue(whiteInk)) {
    return 'clear';
  }
  // DEFAULT 哨兵值表示显式默认白墨，不按自定义白墨图片地址处理。
  if (constant.isDefaultWhiteInkValue(whiteInk)) {
    return 'default';
  }
  // 自定义白墨图层使用独立白墨图参与裁剪和叠加。
  if (whiteInk) {
    return 'custom';
  }
  // 未显式传 whiteInk、null 或空字符串时，统一按默认有白墨处理。
  return 'default';
}

/**
 * 从已渲染的彩墨全画布图派生默认白墨图。
 * @param {string} colorDataURL - 彩墨全画布 dataURL。
 * @returns {Promise<string>} 默认白墨 dataURL。
 */
async function deriveDefaultWhiteInkFromColorImage(colorDataURL) {
  return await canvasUtil.fillNonTransparentPixels({
    imageSrc: colorDataURL,
    rgbaString: constant.whiteInkRgba
  });
}

/**
 * 解析参与逐层合成的图层图片地址。
 * @param {Array<Object>} layers - 图层数组。
 * @param {Object} [options] - 参数对象。
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许烫色专用图层参与绘制。
 * @param {number} options.whiteMode - SKU 默认白墨模式。
 * @returns {Promise<Array<{layer:Object,imageUrl:string,whiteInk:string,whiteInkState:string}>>} 图层合成项。
 */
async function resolveWhiteInkCompositeItems(layers, options) {
  const sortedLayers = sortLayersByZIndex(layers).filter(function (layer) {
    return shouldProcessWhiteInkCompositeLayer(layer, options);
  });
  const itemPromises = sortedLayers.map(async function (layer) {
    return {
      layer,
      imageUrl: await resolveLayerImageUrl(layer),
      whiteInk: getLayerWhiteInkSource(layer),
      whiteInkState: getLayerWhiteInkCompositeState(layer, options)
    };
  });
  const items = await Promise.all(itemPromises);
  return items.filter(function (item) {
    return !!(item && item.imageUrl);
  });
}

/**
 * 判断常规导出口径下是否存在白墨相关状态。
 * @param {Array<Object>} layers - 图层数组。
 * @returns {boolean} 是否存在白墨相关状态。
 */
function hasLegacyWhiteInkLayers(layers) {
  return layers.some(function (layer) {
    return layer && !isDefaultWhiteInk(layer);
  });
}

/**
 * 判断反面对比口径下是否存在白墨相关状态。
 * @param {Array<Object>} layers - 图层数组。
 * @param {Array<string>} currentNoWhiteLayerType - 不参与白墨处理的图层类型。
 * @returns {boolean} 是否存在白墨相关状态。
 */
function hasReverseCompareWhiteInkLayers(layers, currentNoWhiteLayerType) {
  return layers.some(function (layer) {
    return (
      layer &&
      !constant.isClearWhiteInkValue(layer.whiteInk) &&
      !currentNoWhiteLayerType.includes(layer.type)
    );
  });
}

/**
 * 判断逐层融合口径下是否存在可处理图层。
 * @param {Array<Object>} layers - 图层数组。
 * @param {Array<string>} currentNoWhiteLayerType - 不参与白墨处理的图层类型。
 * @returns {boolean} 是否存在可处理图层。
 */
function hasLayeredWhiteInkLayers(layers, currentNoWhiteLayerType) {
  return layers.some(function (layer) {
    return (
      layer &&
      !currentNoWhiteLayerType.includes(layer.type) &&
      hasLayerImageSource(layer)
    );
  });
}

/**
 * 判断是否需要导出最终白墨。
 * @param {Object} options - 参数对象。
 * @param {Array<Object>} options.layers - 图层数组。
 * @param {Array<string>} options.currentNoWhiteLayerType - 不参与白墨处理的图层类型。
 * @param {boolean} [options.reverseCompareGate=false] - 是否使用反面对比口径。
 * @param {boolean} [options.layeredComposite=false] - 是否使用逐层融合口径。
 * @returns {boolean} 是否需要导出。
 */
function shouldExportFinalWhiteInk(options) {
  const {
    layers,
    currentNoWhiteLayerType,
    reverseCompareGate = false,
    layeredComposite = false
  } = options || {};
  // 逐层融合流程需要默认、自定义、清空白墨都进入处理。
  if (layeredComposite) {
    return hasLayeredWhiteInkLayers(layers, currentNoWhiteLayerType);
  }
  // 反面对比保持旧触发口径：清空白墨不触发导出。
  if (reverseCompareGate) {
    return hasReverseCompareWhiteInkLayers(layers, currentNoWhiteLayerType);
  }
  return hasLegacyWhiteInkLayers(layers);
}

/**
 * 把图层几何信息应用到 Fabric 对象上。
 * @param {fabric.Object} fabricObj - Fabric 对象。
 * @param {Object} layer - 图层对象。
 * @returns {void}
 */
function applyLayerGeometryToFabricObject(fabricObj, layer) {
  const layerInfo = layer.layerInfo || {};
  const shouldFitLayerInfoSize =
    fabricObj._fitLayerInfoSize === true ||
    layer.fitLayerInfoSize === true ||
    layer.canvasType === 'textbox' ||
    layer.hasTextImageData === true;
  let renderScaleX = layerInfo.scaleX;
  let renderScaleY = layerInfo.scaleY;

  // 临时预览图或文字 imageData 还原图尺寸与原图尺寸不一致时，按 layerInfo 目标尺寸适配显示。
  if (
    shouldFitLayerInfoSize &&
    fabricObj.width &&
    fabricObj.height &&
    layerInfo.width &&
    layerInfo.height
  ) {
    renderScaleX =
      (layerInfo.width * (layerInfo.scaleX || 1)) / fabricObj.width;
    renderScaleY =
      (layerInfo.height * (layerInfo.scaleY || 1)) / fabricObj.height;
  }

  fabricObj.set({
    left: layerInfo.left || 0,
    top: layerInfo.top || 0,
    scaleX: renderScaleX,
    scaleY: renderScaleY,
    angle: layerInfo.angle || 0,
    originX: layerInfo.originX || 'left',
    originY: layerInfo.originY || 'top',
    opacity: typeof layer.opacity === 'number' ? layer.opacity : 1,
    selectable: false,
    hasControls: false,
    hasBorders: false,
    evented: false
  });
}

/**
 * 将单个图层按全画布坐标导出为 dataURL。
 * @param {Object} options - 参数对象。
 * @param {fabric.StaticCanvas} options.fabricCanvas - 离屏 Fabric 画布。
 * @param {Object} options.layer - 图层对象。
 * @param {string} options.imageUrl - 本次绘制使用的图片地址。
 * @param {number} options.highResolutionMultiplier - 导出倍数。
 * @returns {Promise<string>} 单层全画布 dataURL。
 */
async function renderLayerToDataURL(options) {
  const { fabricCanvas, layer, imageUrl, highResolutionMultiplier } =
    options || {};
  const objectList =
    typeof fabricCanvas.getObjects === 'function'
      ? fabricCanvas.getObjects().slice()
      : [];
  for (let i = 0; i < objectList.length; i++) {
    fabricCanvas.remove(objectList[i]);
  }

  const fabricObj = await loadFabricObjectFromImageUrl(imageUrl);
  applyLayerGeometryToFabricObject(fabricObj, layer);
  fabricCanvas.add(fabricObj);
  fabricCanvas.renderAll();
  return fabricCanvas.toDataURL({
    format: 'png',
    quality: 1,
    multiplier: highResolutionMultiplier
  });
}

/**
 * 把单个图层白墨按彩墨有效区域合成到最终白墨画布。
 * @param {Object} options - 参数对象。
 * @param {CanvasRenderingContext2D} options.resultCtx - 最终白墨上下文。
 * @param {CanvasRenderingContext2D} options.layerCtx - 单层临时上下文。
 * @param {HTMLImageElement} options.colorImage - 彩墨全画布图。
 * @param {HTMLImageElement|null} options.whiteInkImage - 白墨全画布图。
 * @param {number} options.width - 合成宽度。
 * @param {number} options.height - 合成高度。
 * @returns {void}
 */
function composeWhiteInkLayer(options) {
  const { resultCtx, layerCtx, colorImage, whiteInkImage, width, height } =
    options || {};

  // 当前图层彩墨有效区域会覆盖下层白墨，因此先按彩墨 alpha 清空最终白墨。
  resultCtx.save();
  resultCtx.globalCompositeOperation = 'destination-out';
  resultCtx.drawImage(colorImage, 0, 0, width, height);
  resultCtx.restore();

  // 当前图层没有白墨时，只执行清空，不向最终白墨叠加内容。
  if (!whiteInkImage) {
    return;
  }

  layerCtx.clearRect(0, 0, width, height);
  layerCtx.save();
  layerCtx.globalCompositeOperation = 'source-over';
  layerCtx.drawImage(whiteInkImage, 0, 0, width, height);
  layerCtx.globalCompositeOperation = 'destination-in';
  layerCtx.drawImage(colorImage, 0, 0, width, height);
  layerCtx.restore();

  resultCtx.save();
  resultCtx.globalCompositeOperation = 'source-over';
  resultCtx.drawImage(layerCtx.canvas, 0, 0, width, height);
  resultCtx.restore();
}

/**
 * 渲染自定义白墨图层为全画布 dataURL。
 * @param {Object} options - 参数对象。
 * @param {fabric.StaticCanvas} options.fabricCanvas - 离屏 Fabric 画布。
 * @param {Object} options.item - 图层合成项。
 * @param {number} options.highResolutionMultiplier - 导出倍数。
 * @returns {Promise<string>} 自定义白墨 dataURL。
 */
async function renderCustomWhiteInkDataURL(options) {
  const { fabricCanvas, item, highResolutionMultiplier } = options || {};
  return await renderLayerToDataURL({
    fabricCanvas,
    layer: item.layer,
    imageUrl: item.whiteInk,
    highResolutionMultiplier
  });
}

/**
 * 解析当前图层要叠加的白墨 dataURL。
 * @param {Object} options - 参数对象。
 * @param {fabric.StaticCanvas} options.fabricCanvas - 离屏 Fabric 画布。
 * @param {Object} options.item - 图层合成项。
 * @param {string} options.colorDataURL - 当前图层彩墨全画布 dataURL。
 * @param {number} options.highResolutionMultiplier - 导出倍数。
 * @returns {Promise<string>} 白墨 dataURL；没有当前白墨时返回空字符串。
 */
async function resolveLayerWhiteInkDataURL(options) {
  const { fabricCanvas, item, colorDataURL, highResolutionMultiplier } =
    options || {};
  // 清空白墨只清空累计白墨，不叠加当前白墨。
  if (!item || item.whiteInkState === 'clear') {
    return '';
  }
  if (item.whiteInkState === 'custom') {
    return await renderCustomWhiteInkDataURL({
      fabricCanvas,
      item,
      highResolutionMultiplier
    });
  }
  if (item.whiteInkState === 'default') {
    return await deriveDefaultWhiteInkFromColorImage(colorDataURL);
  }
  return '';
}

/**
 * 按图层 zIndex 从低到高逐层合成最终白墨。
 * @param {Object} options - 参数对象。
 * @param {Array} options.layers - 图层数组。
 * @param {Object} options.canvasInfo - 画布信息对象。
 * @param {number} [options.highResolutionMultiplier=1] - 高清倍数。
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许烫色专用图层参与绘制。
 * @param {boolean} [options.reverseCompareGate=false] - 是否使用反面对比的导出触发口径。
 * @param {boolean} [options.layeredComposite=false] - 是否使用逐层融合触发口径。
 * @param {number} options.whiteMode - SKU 默认白墨模式。
 * @returns {Promise<string>} 最终白墨 dataURL；没有白墨图层时返回空字符串。
 */
async function exportFinalWhiteInkByComposite(options) {
  const {
    layers,
    canvasInfo,
    highResolutionMultiplier = 1,
    includeStampingLayers = false,
    reverseCompareGate = false,
    layeredComposite = false,
    whiteMode
  } = options || {};

  // 参数验证。
  if (!layers || !Array.isArray(layers)) {
    throw new Error('exportFinalWhiteInk: layers 参数必须是一个数组');
  }

  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error(
      'exportFinalWhiteInk: canvasInfo 参数必须包含 displayWidth 和 displayHeight'
    );
  }

  // whiteMode 仅保留为合法性校验，未设置 whiteInk 的图层统一按默认有白墨处理。
  if (!validWhiteModes.includes(whiteMode)) {
    throw new Error('exportFinalWhiteInk: whiteMode 参数无效');
  }

  // 按传入口径判断是否存在需要参与白墨处理的图层。
  const currentNoWhiteLayerType = getNoWhiteLayerType({
    includeStampingLayers
  });
  const hasWhiteInkLayers = shouldExportFinalWhiteInk({
    layers,
    currentNoWhiteLayerType,
    reverseCompareGate,
    layeredComposite
  });

  if (!hasWhiteInkLayers) {
    return '';
  }

  let fabricCanvas = null;
  try {
    const canvasWidth = canvasInfo.displayWidth;
    const canvasHeight = canvasInfo.displayHeight;
    const multiplier =
      typeof highResolutionMultiplier === 'number' &&
      highResolutionMultiplier > 0
        ? highResolutionMultiplier
        : 1;
    const exportWidth = Math.max(1, Math.round(canvasWidth * multiplier));
    const exportHeight = Math.max(1, Math.round(canvasHeight * multiplier));

    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = exportWidth;
    resultCanvas.height = exportHeight;
    const resultCtx = resultCanvas.getContext('2d');
    const layerCanvas = document.createElement('canvas');
    layerCanvas.width = exportWidth;
    layerCanvas.height = exportHeight;
    const layerCtx = layerCanvas.getContext('2d');

    if (!resultCtx || !layerCtx) {
      throw new Error('exportFinalWhiteInk: 离屏 canvas 初始化失败');
    }

    fabricCanvas = createOffscreenStaticCanvas({
      width: canvasWidth,
      height: canvasHeight
    });

    const items = await resolveWhiteInkCompositeItems(layers, {
      includeStampingLayers,
      whiteMode
    });
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let colorDataURL = '';
      let whiteInkDataURL = '';
      try {
        colorDataURL = await renderLayerToDataURL({
          fabricCanvas,
          layer: item.layer,
          imageUrl: item.imageUrl,
          highResolutionMultiplier: multiplier
        });
      } catch (error) {
        console.error('exportFinalWhiteInk: 彩墨图层导出失败，已跳过', error);
        continue;
      }

      try {
        whiteInkDataURL = await resolveLayerWhiteInkDataURL({
          fabricCanvas,
          item,
          colorDataURL,
          highResolutionMultiplier: multiplier
        });
      } catch (error) {
        if (item.whiteInkState === 'default') {
          console.error(
            'exportFinalWhiteInk: 默认白墨派生失败，按无白墨处理',
            error
          );
        } else {
          console.error(
            'exportFinalWhiteInk: 白墨图层导出失败，按无白墨处理',
            error
          );
        }
        whiteInkDataURL = '';
      }

      const colorImage = await canvasUtil.loadImage(colorDataURL);
      const whiteInkImage = whiteInkDataURL
        ? await canvasUtil.loadImage(whiteInkDataURL)
        : null;
      composeWhiteInkLayer({
        resultCtx,
        layerCtx,
        colorImage,
        whiteInkImage,
        width: exportWidth,
        height: exportHeight
      });
    }

    return resultCanvas.toDataURL('image/png');
  } catch (error) {
    console.error('exportFinalWhiteInk: 处理失败', error);
    throw error;
  } finally {
    if (fabricCanvas) {
      try {
        fabricCanvas.dispose();
      } catch (error) {
        /* noop */
      }
    }
  }
}

/**
 * 导出最终白墨图片，统一按图层 zIndex 逐层融合。
 * @param {Object} options - 参数对象。
 * @param {Array} options.layers - 图层数组。
 * @param {Object} options.canvasInfo - 画布信息对象。
 * @param {number} [options.highResolutionMultiplier=1] - 高清倍数。
 * @param {boolean} [options.reverseCompareGate=false] - 兼容旧反面对比导出触发口径。
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许烫色专用图层参与绘制。
 * @param {number} options.whiteMode - SKU 默认白墨模式。
 * @returns {Promise<string>} 最终白墨 dataURL；没有白墨图层时返回空字符串。
 */
export async function exportFinalWhiteInk(options) {
  const opts = options || {};
  return await exportFinalWhiteInkByComposite({
    ...opts,
    layeredComposite: true
  });
}
